import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from 'react';
import { isBefore, isAfter, startOfDay } from 'date-fns';
import DateTile from './DateTile';
import CalendarHeader from './CalendarHeader';
import { StyledCalendarGrid } from './styles';
import type { CalendarProps, DateTileProps } from './types';
interface CalendarGridProps extends CalendarProps {
  onDateClick: (date: Date) => void;
  onDateHover: (date: Date | null) => void;
  onDateMouseDown?: (date: Date) => void;
  getDateTileProps: (date: Date) => Partial<DateTileProps>;
  getZagDateProps?: (date: Date) => React.HTMLAttributes<HTMLElement> | undefined;
}
const CalendarGrid = t0 => {
  const $ = _c(51);
  const {
    currentMonth,
    minDate,
    maxDate,
    disabled: t1,
    showHeader: t2,
    firstDayOfWeek: t3,
    onDateClick,
    onDateHover,
    onDateMouseDown,
    getDateTileProps,
    getZagDateProps,
    shouldDisableDate
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const showHeader = t2 === undefined ? true : t2;
  const firstDayOfWeek = t3 === undefined ? 1 : t3;
  let t4;
  if ($[0] !== currentMonth) {
    t4 = currentMonth.getFullYear();
    $[0] = currentMonth;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const year = t4;
  const month = currentMonth.getMonth();
  let days;
  let firstDayOfMonth;
  let t5;
  if ($[2] !== month || $[3] !== year) {
    days = new Date(year, month + 1, 0).getDate();
    firstDayOfMonth = new Date(year, month, 1).getDay();
    t5 = Array.from({
      length: days
    }, (_, i) => new Date(year, month, i + 1));
    $[2] = month;
    $[3] = year;
    $[4] = days;
    $[5] = firstDayOfMonth;
    $[6] = t5;
  } else {
    days = $[4];
    firstDayOfMonth = $[5];
    t5 = $[6];
  }
  const t6 = (firstDayOfMonth - firstDayOfWeek + 7) % 7;
  let t7;
  if ($[7] !== days || $[8] !== t5 || $[9] !== t6) {
    t7 = {
      dates: t5,
      daysInMonth: days,
      leadingOffset: t6
    };
    $[7] = days;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const {
    dates,
    daysInMonth,
    leadingOffset
  } = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = new Date();
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const today = t8;
  let t9;
  if ($[12] !== disabled || $[13] !== maxDate || $[14] !== minDate || $[15] !== shouldDisableDate) {
    t9 = date => {
      if (disabled) {
        return true;
      }
      const day = startOfDay(date);
      if (minDate != null && isBefore(day, startOfDay(minDate))) {
        return true;
      }
      if (maxDate != null && isAfter(day, startOfDay(maxDate))) {
        return true;
      }
      if (shouldDisableDate?.(date) === true) {
        return true;
      }
      return false;
    };
    $[12] = disabled;
    $[13] = maxDate;
    $[14] = minDate;
    $[15] = shouldDisableDate;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const isDateDisabled = t9;
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = date_0 => date_0.getFullYear() === today.getFullYear() && date_0.getMonth() === today.getMonth() && date_0.getDate() === today.getDate();
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const isToday = t10;
  let t11;
  if ($[18] !== currentMonth || $[19] !== showHeader) {
    t11 = showHeader && <CalendarHeader currentMonth={currentMonth} />;
    $[18] = currentMonth;
    $[19] = showHeader;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== leadingOffset) {
    t12 = Array.from({
      length: leadingOffset
    });
    $[21] = leadingOffset;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== currentMonth || $[24] !== t12) {
    t13 = t12.map((__0, i_0) => <span key={`offset-${currentMonth.toISOString()}-${i_0}`} />);
    $[23] = currentMonth;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== dates || $[27] !== daysInMonth || $[28] !== firstDayOfWeek || $[29] !== getDateTileProps || $[30] !== getZagDateProps || $[31] !== isDateDisabled || $[32] !== onDateClick || $[33] !== onDateHover || $[34] !== onDateMouseDown) {
    let t15;
    if ($[36] !== daysInMonth || $[37] !== firstDayOfWeek || $[38] !== getDateTileProps || $[39] !== getZagDateProps || $[40] !== isDateDisabled || $[41] !== onDateClick || $[42] !== onDateHover || $[43] !== onDateMouseDown) {
      t15 = date_1 => {
        const tileProps = getDateTileProps(date_1);
        const zagProps = getZagDateProps?.(date_1);
        const column = (date_1.getDay() - firstDayOfWeek + 7) % 7;
        return <DateTile key={date_1.toISOString()} date={date_1} onClick={onDateClick} onHover={onDateHover} onMouseDown={onDateMouseDown} isToday={isToday(date_1)} isDisabled={isDateDisabled(date_1)} isOutsideMonth={false} isSelected={tileProps.isSelected ?? false} isInRange={tileProps.isInRange ?? false} isRangeStart={tileProps.isRangeStart ?? false} isRangeEnd={tileProps.isRangeEnd ?? false} isRangeEndInComparisonRange={tileProps.isRangeEndInComparisonRange ?? false} isComparisonDate={tileProps.isComparisonDate ?? false} isInComparisonRange={tileProps.isInComparisonRange ?? false} isComparisonRangeStart={tileProps.isComparisonRangeStart ?? false} isComparisonRangeEnd={tileProps.isComparisonRangeEnd ?? false} isOverlap={tileProps.isOverlap ?? false} isOverlapStart={tileProps.isOverlapStart ?? false} isOverlapEnd={tileProps.isOverlapEnd ?? false} isFirstInWeek={column === 0} isLastInWeek={column === 6} isFirstInMonth={date_1.getDate() === 1} isLastInMonth={date_1.getDate() === daysInMonth} zagProps={zagProps} />;
      };
      $[36] = daysInMonth;
      $[37] = firstDayOfWeek;
      $[38] = getDateTileProps;
      $[39] = getZagDateProps;
      $[40] = isDateDisabled;
      $[41] = onDateClick;
      $[42] = onDateHover;
      $[43] = onDateMouseDown;
      $[44] = t15;
    } else {
      t15 = $[44];
    }
    t14 = dates.map(t15);
    $[26] = dates;
    $[27] = daysInMonth;
    $[28] = firstDayOfWeek;
    $[29] = getDateTileProps;
    $[30] = getZagDateProps;
    $[31] = isDateDisabled;
    $[32] = onDateClick;
    $[33] = onDateHover;
    $[34] = onDateMouseDown;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[45] !== t13 || $[46] !== t14) {
    t15 = <StyledCalendarGrid>{t13}{t14}</StyledCalendarGrid>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t11 || $[49] !== t15) {
    t16 = <>{t11}{t15}</>;
    $[48] = t11;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  return t16;
};
CalendarGrid.displayName = 'CalendarGrid';
const MemoizedCalendarGrid = React.memo(CalendarGrid);
MemoizedCalendarGrid.displayName = 'CalendarGrid';
export default MemoizedCalendarGrid;